import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import CreateEvent from "@/pages/CreateEvent";
import EventDetail from "@/pages/EventDetail";
import TributePage from "@/pages/TributePage";
import Recorder from "@/pages/Recorder";
import Settings from "@/pages/Settings";
import Watch from "@/pages/Watch";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AuthCallback from "@/pages/AuthCallback";
import Collaborate from "@/pages/Collaborate";
import StorySetup from "@/pages/StorySetup";
import StoryDetail from "@/pages/StoryDetail";
import StoryInterview from "@/pages/StoryInterview";
import StoryArchive from "@/pages/StoryArchive";
import StoryCollect from "@/pages/StoryCollect";
import StoryScript from "@/pages/StoryScript";
import StoryFilm from "@/pages/StoryFilm";
import StoryInvites from "@/pages/StoryInvites";
import StoryReview from "@/pages/StoryReview";
import GiftRedeem from "@/pages/GiftRedeem";
import RemoteInterview from "@/pages/RemoteInterview";
import AdminShell from "@/pages/admin/AdminShell";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminAI from "@/pages/admin/AdminAI";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminEmails from "@/pages/admin/AdminEmails";
import AdminQueue from "@/pages/admin/AdminQueue";
import AdminSettings from "@/pages/admin/AdminSettings";
import SupportChat from "@/components/SupportChat";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center"><div className="h-10 w-10 rounded-full border-2 border-[hsl(var(--accent))] border-t-transparent animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth redirect: session_id arrives in the URL fragment.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  // Hide the support widget on routes where it would clash with the UI.
  const path = location.pathname;
  const hideChat = (
    /^\/t\/[^/]+\/record$/.test(path) ||    // recorder (space is precious)
    path.startsWith("/payment/") ||         // payment return pages
    path.startsWith("/admin") ||            // admin console has its own AI helper
    path === "/login" || path === "/signup" // auth pages
  );

  return (
    <>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/create" element={<ProtectedRoute><CreateEvent /></ProtectedRoute>} />
        <Route path="/stories/new" element={<ProtectedRoute><StorySetup /></ProtectedRoute>} />
        <Route path="/stories/:storyId" element={<ProtectedRoute><StoryDetail /></ProtectedRoute>} />
        <Route path="/stories/:storyId/interview/:chapterKey" element={<ProtectedRoute><StoryInterview /></ProtectedRoute>} />
        <Route path="/stories/:storyId/archive" element={<ProtectedRoute><StoryArchive /></ProtectedRoute>} />
        <Route path="/stories/:storyId/invites" element={<ProtectedRoute><StoryInvites /></ProtectedRoute>} />
        <Route path="/stories/:storyId/script" element={<ProtectedRoute><StoryScript /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/events/:eventId" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
        <Route path="/t/:slug" element={<TributePage />} />
        <Route path="/t/:slug/record" element={<Recorder />} />
        <Route path="/watch/:slug" element={<Watch />} />
        <Route path="/s/:slug" element={<StoryCollect />} />
        <Route path="/si/:slug" element={<RemoteInterview />} />
        <Route path="/story/:slug" element={<StoryFilm />} />
        <Route path="/sr/:slug" element={<StoryReview />} />
        <Route path="/gift/:code" element={<GiftRedeem />} />
        <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/collaborate" element={<Collaborate />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminShell />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/:userId" element={<AdminUserDetail />} />
          <Route path="events" element={<AdminEvents />} />
          <Route path="queue" element={<AdminQueue />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="coupons" element={<AdminCoupons />} />
          <Route path="pricing" element={<AdminPricing />} />
          <Route path="emails" element={<AdminEmails />} />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="ai" element={<AdminAI />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!hideChat && <SupportChat />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
